import { Btn } from './ui/Btn.jsx';
import { anneeApplicable, lignesDuPays, tauxApplicables } from '../lib/charges';
import { C } from '../theme/palette';

/** Un taux tel que l'écran l'écrit : « 5.3 % ». */
const pct = (valeur) => valeur + ' %';

/**
 * Les taux de charges sociales d'une année, en lecture seule.
 *
 * <p>L'équipe et la comptabilité portaient chacune leur propre éditeur des
 * taux : trois formulaires pour un même réglage. Elles n'en montrent plus
 * qu'un résumé, celui des taux que leurs calculs appliquent, et renvoient
 * aux réglages pour les modifier : un seul endroit, une seule vérité.
 *
 * @param {object} props propriétés du composant
 * @param {object} [props.soc] société
 * @param {number} props.annee année de paie dont les taux s'appliquent
 * @param {Function} [props.onNav] navigation vers une page de l'application
 * @returns {import('react').ReactElement} le résumé
 */
export function ApercuCharges({ soc, annee, onNav }) {
  const T = tauxApplicables(soc, annee);
  const source = anneeApplicable(soc, annee);
  // Hors de Suisse, les cotisations du pays, ligne par ligne, comme la fiche.
  const pays = lignesDuPays(soc, annee);
  const ligne = (titre, couleur, parts) => (
    <div style={{ marginBottom: 7 }}>
      <div
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          color: couleur,
          textTransform: 'uppercase',
          marginBottom: 3,
        }}
      >
        {titre}
      </div>
      {/* Les taux passent à la ligne : sur un iPhone, une ligne insécable
          (« Cotisations du canton 0.181 % ») sortait de la carte, coupée. */}
      <div
        style={{
          fontSize: 10,
          color: C.ink,
          lineHeight: 1.6,
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 10,
          minWidth: 0,
        }}
      >
        {parts
          .filter(([, valeur, optionnel]) => !optionnel || valeur > 0)
          .map(([libelle, valeur]) => (
            <span key={libelle} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {libelle} <b style={{ whiteSpace: 'nowrap' }}>{pct(valeur)}</b>
            </span>
          ))}
      </div>
    </div>
  );

  return (
    <div>
      {pays &&
        ligne(
          `Charges patronales ${annee}`,
          C.amber,
          pays.map((l) => [l.libelle, l.pat, true]),
        )}
      {pays &&
        ligne(
          `Déductions salarié ${annee}`,
          C.red,
          pays.map((l) => [l.libelle, l.sal, true]),
        )}
      {!pays &&
        ligne(`Charges patronales ${annee}`, C.amber, [
          ['AVS/AI/APG', T.avsPat],
          ['AC', T.acPat],
          ['AAP', T.aapPat],
          ['Alloc. fam.', T.afPat],
          ['Frais admin', T.adminPat],
          ['IJM', T.ijmPat, true],
          ['Cotisations du canton', T.cantonPat, true],
        ])}
      {!pays &&
        ligne(`Déductions salarié ${annee}`, C.red, [
          ['AVS/AI/APG', T.avsSal],
          ['AC', T.acSal],
          ['AANP', T.aanpSal],
          ['IJM', T.ijmSal, true],
          ['Cotisations du canton', T.cantonSal, true],
        ])}
      <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5, marginBottom: 9 }}>
        {source === annee
          ? `Taux réglés pour ${annee}.`
          : source == null
            ? `Barème par défaut : rien n'est réglé pour ${annee}.`
            : `Taux de ${source}, toujours en vigueur : rien n'est réglé pour ${annee}.`}{' '}
        {!pays && "Le LPP et l'impôt à la source se règlent par employé, dans sa fiche."}
      </div>
      <Btn
        label="⚙ Modifier dans Réglages"
        sm
        ghost
        color={C.navy}
        full
        onClick={() => onNav && onNav('reglages')}
      />
    </div>
  );
}
